// The order on a Conversations row, as chips (owner, 2026-10-30, agent-v2 DASHBOARD).
//
// « On the conversations I need to see the status of the product and people
// who paid — a small tag. If people ordered again, show the delivered and
// above it a small number like 2 — what matters is the status, clean format. »
//
// Pure, for its test. The worker sends `orders` (lib/conversationOrders.ts):
// the order the chat is about + her other orders, each with its status and
// whether money came in (lib/paidState.ts — the one answer). The labels and
// colours are the app's own (lib/utils ORDER_STATUS_LABELS / _COLORS), so a
// status reads the same here as on the Orders page.
//
// Guard: orderBadge.test.ts
import { ORDER_STATUS_COLORS, ORDER_STATUS_LABELS } from '@/lib/utils';
import type { ConversationListOrder, ConversationOrders } from '@/lib/api';

export interface Chip { label: string; cls: string }

const NEUTRAL = 'bg-gray-100 text-gray-700 border-gray-200';

/** The status chip: the app's label, made short (« En production (fabrication) » → « En production »). */
export function statusChip(status: string | null | undefined): Chip | null {
  const s = String(status ?? '').trim();
  if (!s || s === 'unknown') return null;
  const label = (ORDER_STATUS_LABELS[s] ?? s).replace(/\s*\(.*\)\s*$/, '');
  return { label, cls: ORDER_STATUS_COLORS[s] ?? NEUTRAL };
}

/**
 * « Payé » / « Avance » — only when money came in. A delivered order says
 * « Livrée » already (the courier collected), so it carries no second tag;
 * a cancelled one never says it was paid at a glance.
 */
export function paidTag(o: Pick<ConversationListOrder, 'paid' | 'status'> | null | undefined): Chip | null {
  if (!o?.paid) return null;
  if (o.status === 'delivered' || o.status === 'cancelled') return null;
  return o.paid === 'full'
    ? { label: 'Payé', cls: 'bg-emerald-50 text-emerald-800 border-emerald-200' }
    : { label: 'Avance', cls: 'bg-amber-50 text-amber-800 border-amber-200' };
}

export interface RowBadges {
  /** `#1249` — the order the chat is about. */
  tag: string;
  status: Chip | null;
  paid: Chip | null;
  /** How many orders she has in all — the small number above the chip when > 1. */
  count: number;
  /** Her other orders, newest first, for the tap/hover reveal. */
  others: Array<{ tag: string; status: Chip | null; paid: Chip | null }>;
}

/** Everything the row shows about her orders, or null when she has none. */
export function rowBadges(orders: ConversationOrders | null | undefined): RowBadges | null {
  if (!orders?.current) return null;
  const others = (orders.others ?? []).map((o) => ({ tag: o.name, status: statusChip(o.status), paid: paidTag(o) }));
  return {
    tag: orders.current.name,
    status: statusChip(orders.current.status),
    paid: paidTag(orders.current),
    count: 1 + others.length,
    others,
  };
}

/** The hover text over the count: « #1100 · Livrée, #998 · Annulée ». */
export function othersTitle(b: RowBadges, t: (s: string) => string = (s) => s): string {
  return b.others.map((o) => [o.tag, o.status ? t(o.status.label) : null, o.paid ? t(o.paid.label) : null]
    .filter(Boolean).join(' · ')).join('\n');
}
